import React, { useState, useEffect } from "react";
import { CardBody } from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import styles from "../../styles.scss";
import SectionLabel from "../../common/sectionLabel";
import { Editor, EditorState, RichUtils } from "draft-js";
import "draft-js/dist/Draft.css";
import { LeftSidebarVariablesProp } from "../../common/types/variables";
import RTEEditorButtons from "../../common/rteEditorButtons";
import TooltipColorPicker from "../../common/tooltipColorPicker";
import RteLinkModal from "../../common/rteLinkModal";
import {
  createRteEditorState,
  ensureRteDecorator,
  getLinkEntityKeyAtSelection,
  isLinkActiveAtSelection,
  rteContentStateFromHtml,
  rteHtmlFromContentState,
  rteLinkDecorator,
} from "../../common/rteLinkUtils";

const GivingSideParagraphSection = ({
  variables,
}: LeftSidebarVariablesProp) => {
  const [htmlOutput, setHtmlOutput] = variables["text"];
  const [textColor, setTextColor] = variables["textColor"];
  const [hasLoadedHtml, setHasLoadedHtml] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkEntityKey, setLinkEntityKey] = useState<string | null>(null);

  const [editorState, setEditorState] = useState(() => {
    try {
      if (htmlOutput !== "") {
        return createRteEditorState(rteContentStateFromHtml(htmlOutput));
      }
      return EditorState.createEmpty(rteLinkDecorator);
    } catch (error) {
      console.error("Error parsing HTML:", error);
      return EditorState.createEmpty(rteLinkDecorator);
    }
  });

  useEffect(() => {
    if (!hasLoadedHtml) return;

    const html = rteHtmlFromContentState(editorState.getCurrentContent());
    setHtmlOutput(html);
  }, [editorState, hasLoadedHtml, setHtmlOutput]);

  useEffect(() => {
    if (!hasLoadedHtml && htmlOutput !== "") {
      setEditorState(
        createRteEditorState(rteContentStateFromHtml(htmlOutput || ""))
      );
      setHasLoadedHtml(true);
    }
  }, [htmlOutput, hasLoadedHtml]);

  const onEditorChange = (nextState: EditorState) => {
    setEditorState(ensureRteDecorator(nextState));
  };

  const toggleInlineStyle = (style: string) => {
    onEditorChange(RichUtils.toggleInlineStyle(editorState, style));
  };

  const toggleBlockType = (blockType: string) => {
    onEditorChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  const isInlineStyleActive = (style: string) => {
    return editorState.getCurrentInlineStyle().has(style);
  };

  const isBlockTypeActive = (blockType: string) => {
    const selection = editorState.getSelection();
    const blockKey = selection.getStartKey();
    const currentBlock = editorState
      .getCurrentContent()
      .getBlockForKey(blockKey);
    return currentBlock.getType() === blockType;
  };

  const handleKeyCommand = (command: string, currentState: EditorState) => {
    const newState = RichUtils.handleKeyCommand(currentState, command);
    if (newState) {
      onEditorChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  const openLinkModal = () => {
    setLinkEntityKey(getLinkEntityKeyAtSelection(editorState));
    setLinkModalOpen(true);
  };

  return (
    <CardBody>
      <div className={styles.section}>
        <SectionLabel
          label="Paragraph"
          children={
            <RTEEditorButtons
              isInlineStyleActive={isInlineStyleActive}
              toggleInlineStyle={toggleInlineStyle}
              allowList={true}
              allowLink={true}
              isLinkActive={isLinkActiveAtSelection(editorState)}
              onLinkClick={openLinkModal}
              isBlockTypeActive={isBlockTypeActive}
              toggleBlockType={toggleBlockType}
            />
          }
        />

        <div
          style={{
            padding: "16px",
            minHeight: "400px",
            border: "#E2E8F0 1px solid",
            borderRadius: "12px",
            backgroundColor: "white",
            cursor: "text",
            fontWeight: 300,
          }}
        >
          <Editor
            editorState={editorState}
            onChange={onEditorChange}
            handleKeyCommand={handleKeyCommand}
            placeholder="Start typing your paragraph content..."
          />
        </div>
        <SectionLabel
          label="Text color"
          tooltip="This is the color of the text in the paragraph."
        />
        <TooltipColorPicker value={textColor} setValue={setTextColor} />
      </div>
      <RteLinkModal
        isOpen={linkModalOpen}
        toggle={() => setLinkModalOpen(false)}
        editorState={editorState}
        entityKey={linkEntityKey}
        onComplete={onEditorChange}
      />
    </CardBody>
  );
};

export default withStyles(styles)(GivingSideParagraphSection);
